import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
    ArrowLeft,
    FolderKanban,
    UploadCloud,
    FileText,
    FileSpreadsheet,
    FileCode,
    FileArchive,
    X,
    Loader2,
    CheckCircle2,
    AlertCircle,
    Download,
    RefreshCw,
    Info
} from 'lucide-react';
import { toast } from 'react-hot-toast';

export default function EditProject() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [cohorts, setCohorts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const [formData, setFormData] = useState({
        title: '',
        cohort_id: '',
        description: ''
    });

    const [currentFile, setCurrentFile] = useState<{
        original_name: string;
        formatted_size: string | null;
        file_extension: string;
        file_path: string;
    } | null>(null);

    const [replacementFile, setReplacementFile] = useState<File | null>(null);
    const [dragActive, setDragActive] = useState(false);

    const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

    const ALLOWED_EXTENSIONS = [
        'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv', 'txt', 'zip', 'rar', '7z'
    ];

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                // 1. Load Cohorts
                const cohortsRes = await fetch(`${API_URL}/cohorts`, {
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
                if (cohortsRes.ok) {
                    const cohortsData = await cohortsRes.json();
                    setCohorts(cohortsData);
                }

                // 2. Load Project details
                const projectRes = await fetch(`${API_URL}/instructor/projects/${id}`, {
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                if (!projectRes.ok) {
                    throw new Error('Project not found or accessible.');
                }

                const projectData = await projectRes.json();
                setFormData({
                    title: projectData.title || '',
                    cohort_id: projectData.cohort_id || '',
                    description: projectData.description || ''
                });

                setCurrentFile({
                    original_name: projectData.original_file_name || 'Project File',
                    formatted_size: projectData.formatted_file_size,
                    file_extension: projectData.file_extension || '',
                    file_path: projectData.file_path
                });
            } catch (err: any) {
                console.error('Error loading project details:', err);
                toast.error(err.message || 'Failed to load project details.');
                navigate('/instructor/projects');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            loadData();
        }
    }, [id]);

    const validateAndSetReplacementFile = (file: File) => {
        const ext = file.name.split('.').pop()?.toLowerCase() || '';

        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            toast.error(`Invalid file format (.${ext}). Supported formats: ${ALLOWED_EXTENSIONS.join(', ')}`);
            return false;
        }

        setReplacementFile(file);
        return true;
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            validateAndSetReplacementFile(e.target.files[0]);
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            validateAndSetReplacementFile(e.dataTransfer.files[0]);
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024 * 1024) {
            return (bytes / 1024).toFixed(1) + ' KB';
        }
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    };

    const getFileIcon = (filename: string) => {
        const ext = filename.split('.').pop()?.toLowerCase() || '';
        if (['xls', 'xlsx', 'csv'].includes(ext)) {
            return <FileSpreadsheet className="text-emerald-600" size={28} />;
        }
        if (ext === 'pdf') {
            return <FileText className="text-red-500" size={28} />;
        }
        if (['doc', 'docx'].includes(ext)) {
            return <FileText className="text-blue-500" size={28} />;
        }
        if (['ppt', 'pptx'].includes(ext)) {
            return <FileText className="text-orange-500" size={28} />;
        }
        if (['zip', 'rar', '7z'].includes(ext)) {
            return <FileArchive className="text-purple-500" size={28} />;
        }
        return <FileCode className="text-slate-600" size={28} />;
    };

    const handleDownloadCurrent = async () => {
        if (!id) return;
        try {
            setDownloading(true);
            toast.loading('Downloading project file...', { id: 'edit-dl' });
            const res = await fetch(`${API_URL}/projects/${id}/download`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!res.ok) throw new Error('Failed to download project file.');

            const blob = await res.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = currentFile?.original_name || 'project_file';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
            toast.success('Downloaded successfully!', { id: 'edit-dl' });
        } catch (err: any) {
            toast.error(err.message || 'Error downloading file.', { id: 'edit-dl' });
        } finally {
            setDownloading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.title.trim()) {
            toast.error('Please enter a project title.');
            return;
        }

        if (!formData.cohort_id) {
            toast.error('Please select a target cohort.');
            return;
        }

        setIsSubmitting(true);
        const loadingToast = toast.loading(
            replacementFile ? 'Uploading replacement file and updating...' : 'Saving project updates...'
        );

        try {
            const data = new FormData();
            data.append('title', formData.title.trim());
            data.append('cohort_id', formData.cohort_id);
            if (formData.description.trim()) {
                data.append('description', formData.description.trim());
            }
            if (replacementFile) {
                data.append('project_file', replacementFile);
            }

            // Using POST to support multipart/form-data for Laravel API
            const response = await fetch(`${API_URL}/instructor/projects/${id}`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: data
            });

            const result = await response.json();

            if (response.ok) {
                toast.success('Project updated successfully!', { id: loadingToast });
                navigate('/instructor/projects');
            } else {
                throw new Error(result.message || 'Failed to update project.');
            }
        } catch (err: any) {
            console.error('Project Update Error:', err);
            toast.error(err.message || 'An error occurred while updating the project.', { id: loadingToast });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center p-20">
                <Loader2 className="animate-spin text-brand-emerald mb-4" size={40} />
                <p className="font-bold text-gray-500">Loading project details...</p>
            </div>
        );
    }

    return (
        <div className="edit-project-container">
            <style>{`
                .edit-project-container,
                .staff-scope .edit-project-container {
                    max-width: 860px;
                    margin: 0 auto;
                    font-family: 'Inter', sans-serif;
                    padding: 1rem 1.25rem 4rem 1.25rem;
                    box-sizing: border-box;
                    width: 100%;
                }

                .form-card,
                .staff-scope .form-card {
                    background: var(--index-card-bg);
                    border: 1px solid var(--index-border-color);
                    border-radius: 24px;
                    padding: 2.25rem;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.03);
                    box-sizing: border-box;
                    width: 100%;
                }

                .form-group,
                .staff-scope .form-group {
                    margin-bottom: 1.5rem;
                }

                .form-label,
                .staff-scope .form-label {
                    display: block;
                    font-size: 0.92rem;
                    font-weight: 800;
                    color: var(--index-text-heading);
                    margin-bottom: 0.5rem;
                }

                .form-label .required-dot,
                .staff-scope .form-label .required-dot {
                    color: #ef4444;
                    margin-left: 3px;
                }

                .form-input,
                .form-select,
                .form-textarea,
                .staff-scope .form-input,
                .staff-scope .form-select,
                .staff-scope .form-textarea {
                    width: 100%;
                    padding: 0.85rem 1.15rem;
                    border: 1px solid var(--index-border-color);
                    border-radius: 14px;
                    font-size: 0.95rem;
                    font-weight: 600;
                    background: var(--index-hover-bg);
                    color: var(--index-text-heading);
                    outline: none;
                    transition: all 0.2s;
                    box-sizing: border-box;
                }

                .form-input:focus,
                .form-select:focus,
                .form-textarea:focus,
                .staff-scope .form-input:focus,
                .staff-scope .form-select:focus,
                .staff-scope .form-textarea:focus {
                    border-color: var(--index-primary-color);
                    background: var(--index-card-bg);
                    box-shadow: 0 0 0 3px color-mix(in srgb, var(--index-primary-color) 15%, transparent);
                }

                .current-file-box,
                .staff-scope .current-file-box {
                    background: var(--index-hover-bg);
                    border: 1px solid var(--index-border-color);
                    border-radius: 16px;
                    padding: 1.15rem;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 1rem;
                    gap: 0.75rem;
                    min-width: 0;
                }

                .upload-dropzone,
                .staff-scope .upload-dropzone {
                    border: 2px dashed var(--index-border-color);
                    border-radius: 16px;
                    padding: 2rem 1.25rem;
                    text-align: center;
                    background: var(--index-hover-bg);
                    transition: all 0.2s ease;
                    cursor: pointer;
                }

                .upload-dropzone.active,
                .staff-scope .upload-dropzone.active {
                    border-color: var(--index-primary-color);
                    background: rgba(16, 185, 129, 0.05);
                }

                .upload-dropzone:hover,
                .staff-scope .upload-dropzone:hover {
                    border-color: var(--index-primary-color);
                }

                .btn-submit-action,
                .staff-scope .btn-submit-action {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    background: var(--index-primary-color);
                    color: white;
                    padding: 0.85rem 2rem;
                    min-height: 46px;
                    border-radius: 14px;
                    font-weight: 800;
                    font-size: 0.95rem;
                    border: none;
                    cursor: pointer;
                    transition: all 0.2s;
                    box-shadow: 0 8px 16px -3px color-mix(in srgb, var(--index-primary-color) 30%, transparent);
                }

                .btn-submit-action:hover:not(:disabled),
                .staff-scope .btn-submit-action:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 12px 20px -3px color-mix(in srgb, var(--index-primary-color) 40%, transparent);
                }

                .btn-submit-action:disabled,
                .staff-scope .btn-submit-action:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .form-actions-bar,
                .staff-scope .form-actions-bar {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 0.75rem;
                    padding-top: 1.5rem;
                    border-top: 1px solid var(--index-border-color);
                }

                .btn-cancel-action,
                .staff-scope .btn-cancel-action {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0.85rem 1.5rem;
                    min-height: 46px;
                    border-radius: 14px;
                    border: 1px solid var(--index-border-color);
                    background: var(--index-card-bg);
                    font-size: 0.95rem;
                    font-weight: 700;
                    color: var(--index-text-secondary);
                    text-decoration: none;
                    transition: all 0.2s;
                }

                .btn-cancel-action:hover,
                .staff-scope .btn-cancel-action:hover {
                    background: var(--index-hover-bg);
                    color: var(--index-text-heading);
                }

                @media (max-width: 768px) {
                    .edit-project-container,
                    .staff-scope .edit-project-container {
                        padding: 0.75rem 0.75rem 3rem 0.75rem;
                    }

                    .form-card,
                    .staff-scope .form-card {
                        padding: 1.25rem 1rem;
                        border-radius: 18px;
                    }

                    .form-header-box,
                    .staff-scope .form-header-box {
                        gap: 0.75rem !important;
                        margin-bottom: 1.25rem !important;
                        padding-bottom: 1.25rem !important;
                    }

                    .form-header-box h1,
                    .staff-scope .form-header-box h1 {
                        font-size: 1.35rem !important;
                    }

                    .form-header-box p,
                    .staff-scope .form-header-box p {
                        font-size: 0.82rem !important;
                    }

                    .form-input,
                    .form-select,
                    .form-textarea,
                    .staff-scope .form-input,
                    .staff-scope .form-select,
                    .staff-scope .form-textarea {
                        font-size: 1rem; /* Prevents iOS auto-zoom */
                    }

                    .current-file-box,
                    .staff-scope .current-file-box {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 0.75rem;
                        padding: 0.9rem;
                    }

                    .current-file-box button,
                    .staff-scope .current-file-box button {
                        width: 100%;
                        justify-content: center;
                        padding: 0.65rem;
                        min-height: 40px;
                    }

                    .upload-dropzone,
                    .staff-scope .upload-dropzone {
                        padding: 1.5rem 0.75rem;
                    }

                    .upload-dropzone h5,
                    .staff-scope .upload-dropzone h5 {
                        font-size: 0.9rem !important;
                    }

                    .form-actions-bar,
                    .staff-scope .form-actions-bar {
                        flex-direction: column-reverse;
                        gap: 0.65rem;
                        width: 100%;
                    }

                    .btn-cancel-action,
                    .btn-submit-action,
                    .staff-scope .btn-cancel-action,
                    .staff-scope .btn-submit-action {
                        width: 100%;
                        justify-content: center;
                    }
                }
            `}</style>

            {/* Back Navigation */}
            <div className="mb-5">
                <Link
                    to="/instructor/projects"
                    className="inline-flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-brand-emerald transition-colors"
                >
                    <ArrowLeft size={16} />
                    <span>Back to Projects</span>
                </Link>
            </div>

            <div className="form-card">
                {/* Header */}
                <div className="form-header-box flex items-center gap-3.5 mb-6 pb-6 border-b border-gray-100 dark:border-gray-800">
                    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-brand-emerald flex items-center justify-center flex-shrink-0">
                        <FolderKanban size={22} />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl font-black text-gray-900 dark:text-white m-0">Edit Project</h1>
                        <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm font-semibold m-0 mt-0.5">
                            Update project information or replace the attached file.
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Project Title */}
                    <div className="form-group">
                        <label className="form-label" htmlFor="title">
                            Project Title <span className="required-dot">*</span>
                        </label>
                        <input
                            id="title"
                            type="text"
                            className="form-input"
                            placeholder="e.g. Primavera P6 Scheduling Baseline Project"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            required
                        />
                    </div>

                    {/* Target Cohort */}
                    <div className="form-group">
                        <label className="form-label" htmlFor="cohort_id">
                            Target Cohort <span className="required-dot">*</span>
                        </label>
                        <select
                            id="cohort_id"
                            className="form-select"
                            value={formData.cohort_id}
                            onChange={(e) => setFormData({ ...formData, cohort_id: e.target.value })}
                            required
                        >
                            {cohorts.map((cohort: any) => (
                                <option key={cohort.id} value={cohort.id}>
                                    {cohort.name} ({cohort.course?.title || 'General'})
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-400 font-semibold mt-1.5 flex items-center gap-1">
                            <Info size={13} />
                            <span>Students in this cohort have permission to download this project.</span>
                        </p>
                    </div>

                    {/* Description */}
                    <div className="form-group">
                        <label className="form-label" htmlFor="description">
                            Description <span className="text-xs font-normal text-gray-400">(Optional)</span>
                        </label>
                        <textarea
                            id="description"
                            rows={3}
                            className="form-textarea"
                            placeholder="Provide project guidelines or instructions..."
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />
                    </div>

                    {/* Current File Display */}
                    <div className="form-group">
                        <label className="form-label">Active Project File</label>
                        {currentFile && (
                            <div className="current-file-box">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="p-2 bg-white rounded-xl shadow-xs flex-shrink-0">
                                        {getFileIcon(currentFile.original_name)}
                                    </div>
                                    <div className="min-w-0">
                                        <h4 className="text-sm font-bold text-gray-900 truncate m-0 mb-0.5">
                                            {currentFile.original_name}
                                        </h4>
                                        <p className="text-xs text-gray-500 font-semibold m-0">
                                            {currentFile.formatted_size || 'Attached File'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleDownloadCurrent}
                                    disabled={downloading}
                                    className="px-3.5 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-xs font-bold inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                                >
                                    {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                                    <span>Download</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Replace File Section */}
                    <div className="form-group">
                        <label className="form-label">
                            Replace File <span className="text-xs font-normal text-gray-400">(Optional &mdash; leave empty to keep current file)</span>
                        </label>

                        {!replacementFile ? (
                            <div
                                className={`upload-dropzone ${dragActive ? 'active' : ''}`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                onClick={() => document.getElementById('replace-file-input')?.click()}
                            >
                                <input
                                    id="replace-file-input"
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip,.rar,.7z"
                                    onChange={handleFileChange}
                                />
                                <div className="w-10 h-10 rounded-xl bg-gray-100 text-gray-600 flex items-center justify-center mx-auto mb-2">
                                    <UploadCloud size={20} />
                                </div>
                                <h5 className="text-sm font-bold text-gray-800 m-0 mb-0.5">
                                    Upload a new file to replace the existing one
                                </h5>
                                <p className="text-xs text-gray-500 font-medium m-0">
                                    Supported formats: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, CSV, TXT, ZIP
                                </p>
                            </div>
                        ) : (
                            <div className="p-4 bg-emerald-50/70 border border-emerald-200 rounded-2xl flex items-center justify-between">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="p-2 bg-white rounded-xl shadow-xs flex-shrink-0">
                                        {getFileIcon(replacementFile.name)}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <span className="text-xs font-black uppercase tracking-wider bg-emerald-600 text-white px-2 py-0.5 rounded-md">
                                                New File
                                            </span>
                                            <h4 className="text-sm font-bold text-gray-900 truncate m-0">
                                                {replacementFile.name}
                                            </h4>
                                        </div>
                                        <p className="text-xs text-emerald-800 font-semibold m-0">
                                            {formatFileSize(replacementFile.size)} &bull; Will replace active file upon saving
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setReplacementFile(null)}
                                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                                    title="Cancel replacement"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="form-actions-bar">
                        <Link
                            to="/instructor/projects"
                            className="btn-cancel-action"
                        >
                            Cancel
                        </Link>
                        <button
                            type="submit"
                            disabled={isSubmitting || !formData.title.trim()}
                            className="btn-submit-action"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 size={18} className="animate-spin" />
                                    <span>Saving...</span>
                                </>
                            ) : (
                                <>
                                    <CheckCircle2 size={18} strokeWidth={2.5} />
                                    <span>Save Changes</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
